/**
 * Storage utilities for safe localStorage operations
 * Prevents errors from stale IDs after DB resets
 */

const STORAGE_VERSION_KEY = 'aion_storage_version';
const CURRENT_VERSION = '1.0.0';

/**
 * Check if storage needs to be cleared (version mismatch or corrupted)
 */
export function checkStorageVersion(): boolean {
  try {
    const storedVersion = localStorage.getItem(STORAGE_VERSION_KEY);
    if (storedVersion !== CURRENT_VERSION) {
      console.warn('⚠️ Storage version mismatch - clearing localStorage');
      clearAppStorage();
      localStorage.setItem(STORAGE_VERSION_KEY, CURRENT_VERSION);
      return false; // Storage was cleared
    }
    return true; // Storage is valid
  } catch (error) {
    console.error('Error checking storage version:', error);
    return true;
  }
}

/**
 * Clear all app-related localStorage except authentication
 */
export function clearAppStorage(): void {
  try {
    const token = localStorage.getItem('token');
    const currentUser = localStorage.getItem('currentUser');
    
    // Clear everything
    localStorage.clear();
    
    // Restore authentication
    if (token) localStorage.setItem('token', token);
    if (currentUser) localStorage.setItem('currentUser', currentUser);
    
    // Set new version
    localStorage.setItem(STORAGE_VERSION_KEY, CURRENT_VERSION);
    
    console.log('✅ App storage cleared (auth preserved)');
  } catch (error) {
    console.error('Error clearing storage:', error);
  }
}

/**
 * Safe localStorage get with error handling
 */
export function getStorageItem<T>(key: string, defaultValue: T): T {
  try {
    const item = localStorage.getItem(key);
    if (item === null) return defaultValue;
    return JSON.parse(item) as T;
  } catch (error) {
    console.warn(`Failed to get storage item "${key}":`, error);
    return defaultValue;
  }
}

/**
 * Safe localStorage set with error handling
 */
export function setStorageItem<T>(key: string, value: T): boolean {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (error) {
    console.error(`Failed to set storage item "${key}":`, error);
    return false;
  }
}

/**
 * Remove specific localStorage item
 */
export function removeStorageItem(key: string): boolean {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error(`Failed to remove storage item "${key}":`, error);
    return false;
  }
}

/**
 * Clear all dashboard layouts (useful after DB reset)
 */
export function clearDashboardLayouts(): void {
  try {
    const keys = Object.keys(localStorage);
    const layoutKeys = keys.filter(k => k.startsWith('layout_'));
    layoutKeys.forEach(key => localStorage.removeItem(key));
    console.log(`✅ Cleared ${layoutKeys.length} dashboard layouts`);
  } catch (error) {
    console.error('Error clearing dashboard layouts:', error);
  }
}

/**
 * Initialize storage on app start
 */
export function initStorage(): void {
  checkStorageVersion();
}
